import { FlagConfig } from './flag-catalog.types';
import { FlagCatalogError } from './flag-catalog.error';

/**
 * Validate that an enum/variant flag's default belongs to its declared closed
 * set. `EnumFlagConfig.defaultValue` must be one of `values`; a
 * `VariantFlagConfig.defaultVariant` must be one of `variants`. A typo
 * (`defaultValue: 'system'` with `values: ['light', 'dark']`) would otherwise
 * produce a frozen catalog whose default downstream evaluation cannot represent,
 * so it is a build-time {@link FlagCatalogError}.
 *
 * Both `defineFlags` and `composeCatalog` run this on every entry. `defineFlags`
 * validates the entries it mints; `composeCatalog` re-validates on store because
 * it also accepts hand-rolled/untyped fragments whose entries never passed
 * through `defineFlags` — the re-check defends the composition root against a
 * hand-rolled fragment whose default falls outside its declared set.
 *
 * @param key the fully-qualified flag key, used only for the error message.
 * @param config the entry to validate.
 */
export function assertDefaultMembership(key: string, config: FlagConfig): void {
    if (config.type === 'enum' && !config.values.includes(config.defaultValue)) {
        throw new FlagCatalogError(
            `Flag '${key}' has defaultValue '${config.defaultValue}', which is not one of its ` +
                `declared values: ${config.values.join(', ')}.`
        );
    }
    if (config.type === 'variant' && !config.variants.includes(config.defaultVariant)) {
        throw new FlagCatalogError(
            `Flag '${key}' has defaultVariant '${config.defaultVariant}', which is not one of its ` +
                `declared variants: ${config.variants.join(', ')}.`
        );
    }
}
